import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { useAccountSelectionApiFacade } from '../../apis/facades/useAccountSelectionApiFacade';
import { UserAccount } from '@server/user-account';
import { selectUserAccountId } from '../../reducers/userInfoReducer';
import useSweepNavigate from '../../navigation-utils/useSweepNavigate';
import { replaceAccountIdInPath } from '../../navigation-utils/urlOrgUtils';
import { useSweepNotifications } from '../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { extractShortUUID } from '../../navigation-utils/readableUUID';
import { telemetry } from '../../telemetry';

interface AccountSelectionContextType {
  isOpen: boolean;
  accounts: UserAccount[] | undefined;
  isLoadingAccounts: boolean;
  isAccountReady: boolean;
  isSSOUser: boolean;
  setIsSSOUser: (isSSOUser: boolean) => void;
  openAccountSelection: () => void;
  closeAccountSelection: () => void;
  fetchAccounts: () => Promise<void>;
  removeAccountFromState: (accountId: string) => void;
}

export const AccountSelectionContext = createContext<AccountSelectionContextType | undefined>(
  undefined,
);

export const AccountSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [accounts, setAccounts] = useState<UserAccount[] | undefined>(undefined);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isAccountReady, setIsAccountReady] = useState(false);
  const [isSSOUser, setIsSSOUser] = useState(false);

  const { get_accounts } = useAccountSelectionApiFacade();
  const params = useParams<{ accountId: string }>();
  const readableAccountId = params.accountId;
  const userAccountId = useSelector(selectUserAccountId);
  const location = useLocation();
  const { navigate } = useSweepNavigate();
  const { addNotification } = useSweepNotifications();

  // Resolve fullAccountId from readableAccountId (similar logic to useSweepParams but
  // we cannot use it as it takes stuff from this context)
  let fullAccountId: string | undefined;
  if (readableAccountId && accounts) {
    if (readableAccountId.length !== 36) {
      const shortAccountId = extractShortUUID(readableAccountId);
      if (shortAccountId) {
        const matchingAccount = accounts.find((account) => account.id.startsWith(shortAccountId));
        fullAccountId = matchingAccount?.id;
      }
    } else if (readableAccountId && readableAccountId.length === 36) {
      const account = accounts.find((account) => account.id === readableAccountId);
      fullAccountId = account?.id;
    }
  }

  const openAccountSelection = useCallback(() => setIsOpen(true), []);
  const closeAccountSelection = useCallback(() => setIsOpen(false), []);

  const fetchAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);
    try {
      const accounts = await get_accounts();
      const accountsWithNames = accounts.map((account) => ({
        ...account,
        name: !!account.name ? account.name : 'Untitled',
      }));
      setAccounts(accountsWithNames);
    } catch (error) {
      telemetry.captureError(error, { message: 'Failed to fetch accounts' });
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [get_accounts]);

  const removeAccountFromState = useCallback(
    async (accountId: string) => {
      setAccounts(accounts?.filter((account) => account.id !== accountId));
    },
    [accounts],
  );

  // Fetch accounts if there's an accountId in URL that needs validation
  useEffect(() => {
    if (readableAccountId && !accounts && userAccountId) {
      fetchAccounts();
    }
  }, [readableAccountId, accounts, userAccountId, fetchAccounts]);

  // Validate account after accounts are loaded
  useEffect(() => {
    // Wait for userAccountId to be available
    if (!userAccountId) {
      setIsAccountReady(false);
      return;
    }

    if (!accounts) {
      // If there's an accountId in the URL, we MUST wait for accounts to validate it
      if (readableAccountId) {
        setIsAccountReady(false);
        return;
      }
      // No accountId in URL - safe to proceed without accounts
      setIsAccountReady(true);
      return;
    }

    // Handle account switching if URL accountId differs from current user's account
    if (readableAccountId) {
      // Case 1: fullAccountId is undefined - account not found or invalid
      if (!fullAccountId) {
        addNotification({
          message: "You don't have access to given account or it's invalid",
          variant: SweepNotificationVariant.Error,
        });

        const newPath = replaceAccountIdInPath(location.pathname, userAccountId, readableAccountId);
        navigate(newPath, { replace: true });
        setIsAccountReady(false);
        return;
      }

      // Case 2: fullAccountId exists but is different from current user's account
      if (fullAccountId !== userAccountId) {
        const targetAccount = accounts.find((account) => account.id === fullAccountId);

        if (targetAccount) {
          // Trigger full page reload with organization parameter to switch accounts
          const currentUrl =
            window.location.pathname + window.location.search + window.location.hash;
          const separator = window.location.search ? '&' : '?';
          const newUrl = `${currentUrl}${separator}organization=${targetAccount.authZeroOrgId}`;

          window.location.href = newUrl;
          setIsAccountReady(false);
          return;
        } else {
          addNotification({
            message: "You don't have access to given account or it's invalid",
            variant: SweepNotificationVariant.Error,
          });

          const newPath = replaceAccountIdInPath(
            location.pathname,
            userAccountId,
            readableAccountId,
          );
          navigate(newPath, { replace: true });
          setIsAccountReady(false);
          return;
        }
      }
    }

    // If we reach here, account validation passed
    setIsAccountReady(true);
  }, [
    userAccountId,
    readableAccountId,
    fullAccountId,
    accounts,
    location.pathname,
    navigate,
    addNotification,
  ]);

  return (
    <AccountSelectionContext.Provider
      value={{
        isOpen,
        accounts,
        isLoadingAccounts,
        isAccountReady,
        isSSOUser,
        openAccountSelection,
        closeAccountSelection,
        fetchAccounts,
        removeAccountFromState,
        setIsSSOUser,
      }}
    >
      {children}
    </AccountSelectionContext.Provider>
  );
};

export const useAccountSelectionContext = () => {
  const context = useContext(AccountSelectionContext);
  if (!context) {
    throw new Error('useAccountSelectionContext must be used within AccountSelectionProvider');
  }
  return context;
};
